import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserPlus, Loader2 } from "lucide-react";

const REL_LABELS = {
  spouse: "Spouse", domestic_partner: "Domestic partner", child: "Child", parent: "Parent", other_dependent: "Other dependent",
};

const EMPTY = { first_name: "", last_name: "", relationship: "spouse", date_of_birth: "", gender: "", tobacco_use: false };

export default function Household() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/household").then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ first_name: m.first_name, last_name: m.last_name, relationship: m.relationship,
      date_of_birth: (m.date_of_birth || "").slice(0, 10), gender: m.gender || "", tobacco_use: !!m.tobacco_use });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/household/members/${editing.id}`, form);
        toast.success("Family member updated");
      } else {
        await api.post("/household/members", form);
        toast.success("Family member added");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    try {
      await api.delete(`/household/members/${m.id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 sm:p-10 lg:p-12 max-w-5xl mx-auto animate-rise">
      <PageHeader
        eyebrow={user?.employer_id ? "Household-first" : "My Nauticus"}
        title={user?.employer_id ? "My Household" : "My Nauticus"}
        subtitle={user?.employer_id ? "Add the people you're protecting. We use your household to tailor eligibility and coverage." : "Add the people you're helping with Medicare. We use what you save here to tailor your options."}
        action={
          <Button onClick={openAdd} className="bg-gold text-navy-900 hover:bg-gold-light rounded-full font-bold" data-testid="add-member-button">
            <Plus className="w-4 h-4 mr-1" /> Add member
          </Button>
        }
      />

      {/* Employee (primary) */}
      <div className="bg-navy-800 rounded-xl p-6 mb-4 flex items-center gap-4" data-testid="primary-member">
        <div className="w-12 h-12 rounded-full bg-gold text-navy-900 flex items-center justify-center font-bold">
          {(user?.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("")}
        </div>
        <div className="flex-1">
          <p className="text-white font-semibold">{user?.name}</p>
          <p className="text-mist-300 text-sm">{user?.employer_id ? "Primary member · Employee" : "Account owner"}</p>
        </div>
        <span className="text-xs text-mist-300">{user?.email}</span>
      </div>

      {/* Members */}
      <div className="space-y-3">
        {loading ? (
          <>
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </>
        ) : data?.members?.length ? (
          data.members.map((m) => (
            <div key={m.id} className="bg-white rounded-xl border border-mist-200 p-5 flex items-center gap-4" data-testid={`member-${m.id}`}>
              <div className="w-12 h-12 rounded-full bg-mist-100 text-navy-800 flex items-center justify-center font-bold">
                {m.first_name[0]}{m.last_name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-navy-900">{m.first_name} {m.last_name}</p>
                <p className="text-sm text-muted-foreground">
                  {REL_LABELS[m.relationship]} {m.age != null && `· ${m.age} yrs`}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => openEdit(m)} data-testid={`edit-member-${m.id}`}>
                  <Pencil className="w-4 h-4 text-navy-700" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(m)} data-testid={`delete-member-${m.id}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            </div>
          ))
        ) : (
          <div className="bg-white rounded-xl border border-dashed border-mist-300 p-12 text-center" data-testid="empty-household">
            <UserPlus className="w-10 h-10 text-mist-300 mx-auto mb-3" />
            <p className="font-semibold text-navy-900">No family members yet</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Add your spouse, partner, or children to get started.</p>
            <Button onClick={openAdd} className="bg-navy-800 text-white rounded-full" data-testid="empty-add-member">Add your first member</Button>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white" data-testid="member-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit member" : "Add family member"}</DialogTitle>
            <DialogDescription>Add a spouse, partner, child, or dependent to your household.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-semibold">First name</Label>
                <Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1.5" data-testid="member-first-name" />
              </div>
              <div>
                <Label className="font-semibold">Last name</Label>
                <Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1.5" data-testid="member-last-name" />
              </div>
            </div>
            <div>
              <Label className="font-semibold">Relationship</Label>
              <Select value={form.relationship} onValueChange={(v) => setForm({ ...form, relationship: v })}>
                <SelectTrigger className="mt-1.5" data-testid="member-relationship"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(REL_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-semibold">Date of birth</Label>
                <Input type="date" required value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="mt-1.5" data-testid="member-dob" />
              </div>
              <div>
                <Label className="font-semibold">Gender <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} className="mt-1.5" data-testid="member-gender" />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} className="bg-gold text-navy-900 hover:bg-gold-light font-bold" data-testid="save-member">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? "Save changes" : "Add member"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
